import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronIcon, PencilIcon, PlusIcon, SearchIcon } from '@/components/catalog/icons';
import { ApiKeyFormModal } from '@/components/api-keys/ApiKeyFormModal';
import { ApiKeyTokenDialog } from '@/components/api-keys/ApiKeyTokenDialog';
import { REVOKE_CONSEQUENCE, rateLimitLabel } from '@/components/api-keys/api-key-rules';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import type { ApiKey, ApiKeyCreated, ApiKeyListParams, Paginated } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * API keys — `GET /api-keys`, plus create, edit and revoke.
 *
 * A key is the credential a SERVER presents, as opposed to the JWT a browser
 * carries, and it authenticates as a real `d_users` row: `req.user.id` is read
 * in 149 places server-side and `f_api_logs.user_id` is a foreign key into
 * `d_users`, so a key that authenticated as nothing would break those call sites
 * or violate that constraint on every logged request. What the mechanism buys
 * over a password login is **no password, revocation without deleting a person,
 * per-key scopes and per-key rate limits** — and nothing else. Read it as a
 * password with a narrower blast radius, not as a lesser thing.
 *
 * ── Who can open it ───────────────────────────────────────────────────────
 * SUPER-ADMIN ONLY. Every verb on this resource is `super_admin` server-side
 * too, and this app matches rather than loosens: a key issued here can act as
 * somebody else against every endpoint their role reaches, so the gate is
 * `ability.isSuperAdmin` and never `ability.isAdmin`. Same shape, and the same
 * reasoning, as `/permission-sets`.
 *
 * ⚠️ **THE ROUTE CARRIES NO GUARD**, so somebody arriving by URL reaches this
 * component. They meet the page's OWN refusal panel below — never an error,
 * never a blank screen — and **no request is issued at all**, because an empty
 * table would read as "there are no keys", which is a different and false
 * statement on the one screen where it would be alarming.
 *
 * A real 403 is still handled: the hidden control is the optimisation and the
 * server's refusal is the guarantee, because a role can change under a live
 * access token and that token lasts days. A 403 body carries NO `details`, so
 * it renders as a banner and never as a field error.
 *
 * ── The token ─────────────────────────────────────────────────────────────
 * 🔴 **THE PLAINTEXT IS RETURNED EXACTLY ONCE, BY THE CREATE CALL, AND CANNOT
 * BE RECOVERED.** `ApiKey` — the list row — has no token field at all, and that
 * is STRUCTURAL rather than a convention: a cell on this page cannot render one
 * by accident and `tsc` refuses a call site that tries. Only `ApiKeyCreated`
 * carries it, it goes straight to `ApiKeyTokenDialog`, and it is never logged,
 * stored, put in a `title` or included in an error message.
 *
 * ── There is no delete, and its absence is the contract ────────────────────
 * Revoking takes a key out of service and KEEPS THE ROW, so what a key was
 * allowed to do is still answerable after it stops working — which is what lets
 * `f_api_logs` resolve who made a call last month. Do not add a delete, and do
 * not word revocation as one.
 */

const DEFAULT_PER_PAGE = 50;
const MAX_PER_PAGE = 200;
const SKELETON_ROWS = 4;

/**
 * The floor the table lays out to, in px.
 *
 * 🔴 **AN INLINE STYLE, NEVER A CLASS.** Tailwind v4 scans SOURCE TEXT, so a
 * computed `min-w-[…]` emits no rule at all — the class lands in the DOM and
 * does nothing, which looks exactly like a table that chose to have no floor.
 *
 * ⚠️ **EACH TERM IS THE PIXEL VALUE OF THE HEADER CLASS BESIDE IT, AND THE PAIR
 * IS LOAD-BEARING** — `w-36` is `36 × 0.25rem` = 144px. The class is what the
 * browser lays out with; the number is what this floor is summed from, and they
 * are written out together so the arithmetic cannot drift from the rendering.
 * **Move both, or the table scrolls sideways with empty space in it.**
 *
 * ⚠️ **Every value is one Tailwind already ships, deliberately.** v4 would
 * resolve an off-scale `w-30` from its dynamic spacing scale, but no other file
 * in this repo uses one — and a width class that emitted nothing would land in
 * the DOM, do nothing, and look exactly like a column that chose not to have a
 * width. Staying on the scale the rest of the app uses costs a few pixels of
 * precision and removes that failure entirely.
 *
 * `Key` is the one fluid column — it carries no `w-*` and takes whatever the
 * other seven leave — so it contributes a FLOOR rather than a width.
 */
const KEY_COLUMN_FLOOR = 200;
const TABLE_MIN_WIDTH =
  KEY_COLUMN_FLOOR + // Key        (fluid)
  144 + // Prefix     w-36
  176 + // Acts as    w-44
  224 + // Scopes     w-56
  128 + // Rate limit w-32
  144 + // Last used  w-36
  112 + // Status     w-28
  128; // Actions    w-32

const TABLE_STYLE = { minWidth: `${TABLE_MIN_WIDTH}px` };

/**
 * The `revoked` filter.
 *
 * ⛔ **OPT-IN WITH NO DEFAULT, AND A SCREEN MUST NOT DEFAULT IT TO `false` TO
 * "HIDE DEAD KEYS".** Revoked rows are kept precisely so somebody can find the
 * key behind a call made last month, and a default would make the one
 * population this resource exists to preserve unreachable through the one
 * control built to find it — silently, in the direction of a shorter list that
 * reads as a tidy estate.
 *
 * ⚠️ **BOTH VALUES GENUINELY NARROW**, so this is a real three-way choice
 * rather than a tick that is either sent or omitted. The unselected row is the
 * `emptyOptionLabel`, whose string is shared with the trigger's `placeholder`:
 * a reader who sees "Live and revoked" in the list but a blank trigger reads
 * the filter as broken rather than as cleared.
 */
const REVOKED_ANY_LABEL = 'Live and revoked';
const REVOKED_OPTIONS: ComboboxOption[] = [
  { value: 'false', label: 'Live only' },
  { value: 'true', label: 'Revoked only' },
];

function KeyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <circle cx="7.5" cy="15.5" r="3.5" />
      <path d="M10 13 20.5 2.5" />
      <path d="M17 6l2.5 2.5" />
      <path d="M14.5 8.5 17 11" />
    </svg>
  );
}

/** Reads a positive integer out of the query string, ignoring anything else. */
function parsePositiveInt(raw: string | null): number | null {
  if (!raw) return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Tri-state: `undefined` means the key is absent and the filter is OFF, which
 * is not the same as `false`. Anything that is not exactly 'true' or 'false' is
 * treated as absent rather than guessed at — a stale link carrying rubbish must
 * not silently narrow a credential list.
 */
function parseBool(raw: string | null): boolean | undefined {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

interface ParsedQuery {
  page: number;
  perPage: number;
  search: string;
  revoked: boolean | undefined;
}

function parseQuery(params: URLSearchParams): ParsedQuery {
  const perPage = parsePositiveInt(params.get('perPage'));
  return {
    page: parsePositiveInt(params.get('page')) ?? 1,
    // Clamped rather than passed through: above the ceiling is a 422, and a 422
    // from somebody's stale bookmark is a worse answer than a full page.
    perPage: perPage === null ? DEFAULT_PER_PAGE : Math.min(perPage, MAX_PER_PAGE),
    search: params.get('search') ?? '',
    revoked: parseBool(params.get('revoked')),
  };
}

/** Value for the tri-state filter: '' | 'true' | 'false'. */
function boolValue(value: boolean | undefined): string {
  return value === undefined ? '' : String(value);
}

/** Live, or revoked and when. */
function StatusCell({ apiKey }: { apiKey: ApiKey }) {
  if (apiKey.revokedAt === null) {
    return (
      <Badge tone="success" dot>
        Live
      </Badge>
    );
  }
  return (
    <div className="grid gap-1">
      <Badge tone="danger" dot>
        Revoked
      </Badge>
      <span className="text-xs text-fg-muted tabular">{formatDateTime(apiKey.revokedAt)}</span>
    </div>
  );
}

/**
 * What the key may do.
 *
 * ⛔ **AN EMPTY SET IS A REAL AND VALID STATE AND IS NOT RENDERED AS A FAULT.**
 * A key with no scopes authenticates and can do nothing — a soft revoke that
 * starts working again the moment a scope is granted back. So the badge is
 * `neutral` rather than `warning`, and it is a badge rather than an em dash:
 * a dash would read as "not recorded", which would send somebody looking for a
 * display bug instead of at the key.
 */
function ScopesCell({ scopes }: { scopes: readonly string[] }) {
  if (scopes.length === 0) {
    return (
      <Badge tone="neutral" variant="outline">
        No scopes
      </Badge>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {scopes.map((scope) => (
        <code
          key={scope}
          className="rounded-sm border border-border bg-surface-sunken px-1.5 py-0.5 text-[11px] text-fg-secondary [overflow-wrap:anywhere]"
        >
          {scope}
        </code>
      ))}
    </div>
  );
}

export function ApiKeysPage() {
  const { ability } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [result, setResult] = useState<Paginated<ApiKey> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** A 403 is a role change under a live token, not a validation failure. */
  const [forbidden, setForbidden] = useState<string | null>(null);

  const [formState, setFormState] = useState<{ apiKey?: ApiKey } | null>(null);
  /**
   * The create response, held only for as long as its dialog is open.
   *
   * ⛔ **THIS IS THE ONLY PLACE THE PLAINTEXT EXISTS IN THIS APP, and it exists
   * in memory for one dialog.** It is cleared on close, so a re-render cannot
   * bring it back and nothing else on the page can reach it. ⛔ It is never
   * written to `localStorage`, to the URL, or to a log.
   */
  const [issued, setIssued] = useState<ApiKeyCreated | null>(null);

  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  /*
   * The gate. `isSuperAdmin` — not `isAdmin`. A key acts as a user against the
   * whole API, so this app will not offer the controls to a tier that might
   * otherwise pass.
   */
  /*
   * 🔴 2026-10-01: the `api-keys` module (only the super-admin set holds it
   * today). *SUPERSEDED, the comment above: `ability.isSuperAdmin`.* `canManage`
   * is now the READ gate for the page; each control asks its own action, which
   * is what the routes ask (`create`, `update`, and `delete` for revoke).
   */
  const canManage = ability.canRead('api-keys');
  const canCreateKey = ability.canCreate('api-keys');
  const canEditKey = ability.canUpdate('api-keys');
  const canRevokeKey = ability.canDelete('api-keys');

  const queryString = searchParams.toString();
  const query = useMemo(() => parseQuery(new URLSearchParams(queryString)), [queryString]);
  const { page, perPage, search, revoked } = query;

  const load = useCallback(
    (signal?: AbortSignal) => {
      const current = parseQuery(new URLSearchParams(queryString));
      setLoading(true);
      /*
       * ⚠️ **Blank keys are OMITTED rather than sent empty.** This query schema
       * is `.strict()` with no refinement above it, so a bare `?search=` is a
       * **422 naming the key** rather than "no filter" — and on a credential
       * list a refusal that takes the whole table out reads as "no keys exist".
       * `listQuery` drops empties too; this is the belt to that.
       */
      const params: ApiKeyListParams = { page: current.page, perPage: current.perPage };
      if (current.search) params.search = current.search;
      if (current.revoked !== undefined) params.revoked = current.revoked;
      return api
        .listApiKeys(params, signal)
        .then((data) => {
          setResult(data);
          setError(null);
          setForbidden(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 403) {
            /*
             * The token is still valid and the role behind it no longer reaches
             * API keys. No `details` on a 403, so nothing to hang on a field —
             * the whole surface goes away with a banner saying why.
             */
            setForbidden(
              caught.message || 'Your account no longer has permission to manage API keys.',
            );
            setError(null);
            setResult(null);
            return;
          }
          setForbidden(null);
          setError(caught instanceof Error ? caught.message : 'Failed to load API keys');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [queryString],
  );

  useEffect(() => {
    // No request at all without super-admin. The refusal panel below renders
    // instead, and it never reads `loading`.
    if (!canManage) return;
    const controller = new AbortController();
    // 250ms, because the search box writes to the URL as you type and a
    // substring must not cost a request per keystroke.
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [canManage, load]);

  const applyPatch = useCallback(
    (patch: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === '') next.delete(key);
        else next.set(key, value);
      }
      // Any change to what is being asked for invalidates the offset.
      if (!('page' in patch)) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const goToPage = useCallback(
    (target: number) => {
      applyPatch({ page: target <= 1 ? '' : String(target) });
    },
    [applyPatch],
  );

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;
  const anyFilter = search !== '' || revoked !== undefined;

  async function confirmRevoke() {
    if (!pendingRevoke) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await api.revokeApiKey(pendingRevoke.id);
      setPendingRevoke(null);
      /*
       * Re-read rather than patching the row to "revoked" locally. The server
       * hands the WHOLE record back, and beyond that the `revoked` filter and
       * the paging are both server-side — a just-revoked key legitimately
       * belongs on a different page, or on none of them when the list is
       * narrowed to live keys.
       */
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setRevokeError(
          caught.message || 'Your account no longer has permission to revoke API keys.',
        );
      } else if (caught instanceof ApiError) {
        // Verbatim. An already-revoked key and a race both come back naming
        // what happened, and the server's sentence beats anything invented
        // here.
        setRevokeError(caught.message);
      } else {
        setRevokeError('Could not reach the server. The key has NOT been revoked.');
      }
    } finally {
      setRevoking(false);
    }
  }

  function handleCreated(created: ApiKeyCreated) {
    /*
     * The token dialog opens FIRST and the list is reloaded behind it. The
     * reverse order would put a network round trip between the create and the
     * one moment the plaintext can be read — and a failed reload would then
     * take the token with it.
     */
    setIssued(created);
    if (page !== 1) goToPage(1);
    else void load();
  }

  /* ── Not a super-admin: explain, and never fetch ───────────────────────── */

  if (!canManage) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="Access control" title="API keys" />
        <Panel aria-labelledby="api-keys-denied-heading">
          <PanelHeader id="api-keys-denied-heading" title="API keys" />
          <EmptyState
            icon={<KeyIcon />}
            title="Not available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  /* ── The page ──────────────────────────────────────────────────────────── */

  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'key' : 'keys'}${search ? ` matching “${search}”` : ''}`;

  function renderActions(apiKey: ApiKey): ReactNode {
    return (
      <div className="flex flex-wrap justify-end gap-1.5">
        {canEditKey && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            leadingIcon={<PencilIcon />}
            aria-label={`Edit ${apiKey.name}`}
            title={`Edit ${apiKey.name}`}
            onClick={() => setFormState({ apiKey })}
          />
        )}
        {/*
          * No revoke control on an already-revoked key. `revokedAt` is set once
          * and never cleared, so pressing it could only ever produce a refusal
          * — and a button whose one outcome is an error is worse than no
          * button, the same ruling as the delete slot on a system permission
          * set. The dash carries the reason, which is where a reader hunting
          * for the missing control is actually looking.
          */}
        {!canRevokeKey ? null : apiKey.revokedAt === null ? (
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setRevokeError(null);
              setPendingRevoke(apiKey);
            }}
          >
            Revoke
          </Button>
        ) : (
          <span
            className="grid h-7 min-w-7 place-items-center px-1 text-xs text-fg-subtle"
            title="Already revoked. A revoked key stays listed so the audit trail still resolves it."
            aria-hidden="true"
          >
            —
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader
        eyebrow="Access control"
        title="API keys"
        actions={
          canCreateKey ? (
            <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
              New key
            </Button>
          ) : undefined
        }
      />

      <Panel className="min-w-0" aria-labelledby="api-keys-heading">
        <PanelHeader id="api-keys-heading" title="All keys" description={countLabel} />

        {/*
          * Filters get a full-width row of their own rather than PanelHeader's
          * `actions` slot: that slot is `shrink-0`, so its contents can neither
          * shrink nor wrap and a bank of controls takes the page sideways below
          * 768px. This row wraps freely and works at 320px.
          */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field label="Search API keys" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  /*
                   * The endpoint matches the name AND the token prefix, so the
                   * placeholder can promise both. That pairing matters more
                   * than it looks: the prefix is the only part of a key
                   * anybody ever sees again — in a log line, in a report from
                   * `admin-app`, in a screenshot — so the identifier this app
                   * prints has to be the identifier it can be looked up by.
                   */
                  placeholder="Search name or prefix…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(e) => applyPatch({ search: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-44">
            <Field label="Filter by status" hideLabel>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={boolValue(revoked)}
                  onChange={(next) => applyPatch({ revoked: next })}
                  options={REVOKED_OPTIONS}
                  /* Default: BOTH. See `REVOKED_ANY_LABEL` — revoked keys must
                     not be hidden by a filter nobody set. */
                  emptyOptionLabel={REVOKED_ANY_LABEL}
                  placeholder={REVOKED_ANY_LABEL}
                  searchPlaceholder="Search statuses…"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
          {anyFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {forbidden && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4"
            title="Your account can no longer manage API keys."
          >
            <p className="m-0 min-w-0 [overflow-wrap:anywhere]">
              {forbidden} Sign out and back in to pick up the change.
            </p>
          </Notice>
        )}

        {error && (
          <Notice tone="danger" role="alert" className="m-4" title={<>Couldn&rsquo;t load API keys.</>}>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        {!forbidden && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {initialLoad ? (
              <TableWrap frozenHeader>
                <Table style={TABLE_STYLE}>
                  <ApiKeysTableHead />
                  <TBody>
                    {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                      <TR key={i}>
                        <TD>
                          <div className="grid gap-1.5">
                            <Skeleton className="h-3 w-40" />
                            <Skeleton className="h-2.5 w-24" />
                          </div>
                        </TD>
                        <TD>
                          <Skeleton className="h-3 w-24" />
                        </TD>
                        <TD>
                          <Skeleton className="h-3 w-28" />
                        </TD>
                        <TD>
                          <Skeleton className="h-4 w-40" />
                        </TD>
                        <TD>
                          <Skeleton className="h-3 w-16" />
                        </TD>
                        <TD>
                          <Skeleton className="h-3 w-24" />
                        </TD>
                        <TD>
                          <Skeleton className="h-4 w-16" />
                        </TD>
                        <TD numeric>
                          <div className="flex justify-end gap-1.5">
                            <Skeleton className="h-6 w-6" />
                            <Skeleton className="h-6 w-16" />
                          </div>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            ) : rows.length === 0 ? (
              <ApiKeysEmptyState
                filtered={anyFilter}
                hasError={Boolean(error)}
                onClear={clearFilters}
                onCreate={() => setFormState({})}
              />
            ) : (
              <TableWrap frozenHeader>
                <Table style={TABLE_STYLE}>
                  <ApiKeysTableHead />
                  <TBody>
                    {rows.map((apiKey) => (
                      <TR key={apiKey.id} interactive>
                        <TD>
                          <div className="min-w-0">
                            <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                              {apiKey.name}
                            </p>
                            {/*
                              * `purpose` is a grouping LABEL and is display and
                              * audit only — it records what a key was issued
                              * for so the reader knows which one they are about
                              * to revoke. ⛔ Nothing branches on it; the
                              * capability is the scopes column and only that.
                              */}
                            <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                              {apiKey.purpose}
                            </p>
                          </div>
                        </TD>
                        <TD>
                          {/*
                            * The stored 12-character prefix. ⚠️ **An
                            * IDENTIFIER, not a credential** — it is rendered
                            * plainly rather than masked, because it is the only
                            * part of the token that survives creation and the
                            * only thing two keys can be told apart by on
                            * screen. It is not unique, so it is never the sole
                            * discriminator in a destructive dialog.
                            */}
                          <code className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
                            {apiKey.tokenPrefix}…
                          </code>
                        </TD>
                        <TD>
                          <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
                            {apiKey.user.name.trim() || apiKey.user.email}
                          </span>
                        </TD>
                        <TD>
                          <ScopesCell scopes={apiKey.scopes} />
                        </TD>
                        <TD className="text-fg-secondary tabular">
                          {rateLimitLabel(apiKey.rateLimitPerMinute)}
                        </TD>
                        <TD className="text-fg-secondary tabular">
                          {/*
                            * ⛔ **`null` IS "NOTHING RECORDED", NOT "NEVER
                            * USED", AND MUST NOT BE WORDED AS THE SECOND.** It
                            * is derived from a trail nobody has promised to
                            * keep for ever — the same ruling `User.lastActiveAt`
                            * carries — so an em dash is the honest rendering
                            * and a sentence would be a claim about the key.
                            * ⛔ It gates nothing: a key quiet for a month is a
                            * form nobody filled in, not a credential to
                            * withdraw.
                            */}
                          {apiKey.lastUsedAt === null ? (
                            <span className="text-fg-subtle">—</span>
                          ) : (
                            formatDateTime(apiKey.lastUsedAt)
                          )}
                        </TD>
                        <TD>
                          <StatusCell apiKey={apiKey} />
                        </TD>
                        <TD numeric>{renderActions(apiKey)}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </div>
        )}

        {!forbidden && result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No keys to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <ApiKeyFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        apiKey={formState?.apiKey}
        onCreated={handleCreated}
        onSaved={() => void load()}
      />

      <ApiKeyTokenDialog
        open={issued !== null}
        onClose={() => setIssued(null)}
        token={issued?.token ?? ''}
        keyName={issued?.name ?? ''}
      />

      <ConfirmDialog
        open={pendingRevoke !== null}
        onClose={() => {
          setPendingRevoke(null);
          setRevokeError(null);
        }}
        onConfirm={() => void confirmRevoke()}
        tone="danger"
        title={pendingRevoke ? `Revoke ${pendingRevoke.name}?` : 'Revoke API key?'}
        description={
          <>
            {/*
              * ⛔ **PROTECTED — a helper-text sweep must not take this.** It
              * names what a destructive action actually does, what it
              * deliberately does NOT do, and that there is no way back. See
              * `REVOKE_CONSEQUENCE`.
              */}
            <p className="m-0">{REVOKE_CONSEQUENCE}</p>
            {pendingRevoke && (
              /*
               * The purpose and the prefix, so the reader confirms they are
               * stopping the credential they meant to — two keys on one
               * integration are told apart by nothing else in this dialog. ⚠️
               * The prefix is NOT unique, which is why the key's own name is in
               * the title above rather than this line standing alone.
               */
              <p className="mt-2 mb-0 text-xs text-fg-muted">
                Issued for {pendingRevoke.purpose}, acting as{' '}
                {pendingRevoke.user.name.trim() || pendingRevoke.user.email} —{' '}
                <code className="[overflow-wrap:anywhere]">{pendingRevoke.tokenPrefix}…</code>
              </p>
            )}
          </>
        }
        confirmLabel="Revoke key"
        loading={revoking}
        error={revokeError && <span className="[overflow-wrap:anywhere]">{revokeError}</span>}
      />
    </div>
  );
}

function ApiKeysTableHead() {
  return (
    <THead>
      <TR>
        <TH>Key</TH>
        <TH className="w-36">Prefix</TH>
        <TH className="w-44">Acts as</TH>
        <TH className="w-56">Scopes</TH>
        <TH className="w-32">Rate limit</TH>
        <TH className="w-36">Last used</TH>
        <TH className="w-28">Status</TH>
        <TH className="w-32" numeric>
          Actions
        </TH>
      </TR>
    </THead>
  );
}

function ApiKeysEmptyState({
  filtered,
  hasError,
  onClear,
  onCreate,
}: {
  filtered: boolean;
  hasError: boolean;
  onClear: () => void;
  onCreate: () => void;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<KeyIcon />}
        title="Nothing to show"
        description="Couldn’t load the list — see the message above."
      />
    );
  }

  if (filtered) {
    return (
      <EmptyState
        icon={<KeyIcon />}
        title="No keys match those filters"
        description="Try a different term, or clear the filters."
        action={
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        }
      />
    );
  }

  /*
   * ⚠️ **"No keys" is a real and correct state, and the copy says so plainly
   * rather than implying any are on their way.** Nothing seeds this table: a
   * key exists only because a super-administrator issued one.
   */
  return (
    <EmptyState
      icon={<KeyIcon />}
      title="No API keys yet"
      description="Nothing outside SEED EMERGE can call the API until a key is issued."
      action={
        <Button variant="secondary" size="sm" leadingIcon={<PlusIcon />} onClick={onCreate}>
          New key
        </Button>
      }
    />
  );
}
